import { LoadingState } from "@material-dev/insights-portal-ui-components/lib/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { TextVariablesClient, ITextVariablesClient, TextVariableDto } from "app/ApiClient";
import { initialTextVariables } from "./textVariablesState";
import { RootState } from "@store";

const textVariablesClient: ITextVariablesClient = new TextVariablesClient(
  process.env.REACT_APP_API_URL,
);

export const getTextVariablesByProjectId = createAsyncThunk(
  "textVariables/getByProjectId",
  async ({ projectId, versionId, forceFetch }: { projectId: number; versionId: string; forceFetch?: boolean },
    { rejectWithValue, getState },
  ) => {
    try {
      const { textVariables } = getState() as RootState;
      let allTextVariables: TextVariableDto[] = textVariables.allTextVariables;
      if (!allTextVariables?.length || forceFetch) {
        allTextVariables = await textVariablesClient.getTextVariables(projectId, versionId);
      }
      const textVariablesFilters = (textVariables.filters as TextVariableDto) ?? {};

      let filteredData: TextVariableDto[] = [...allTextVariables];

      filteredData = filterTextVariables(textVariablesFilters, filteredData);

      return {
        allTextVariables,
        filteredData,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const filterTextVariables = (textVariablesFilters: TextVariableDto, filteredData: TextVariableDto[]) => {
  if (Object.keys(textVariablesFilters).length > 0) {
    const result = filteredData.filter((item: TextVariableDto) =>
      Object.keys(textVariablesFilters).every((key) => {
        const filterValues = textVariablesFilters[key as keyof TextVariableDto];
        if (Array.isArray(filterValues) && filterValues.length > 0) {
          const fieldValue = item[key as keyof TextVariableDto];
          const actualFieldValue = fieldValue == undefined ? "" : fieldValue;
          return filterValues.includes(actualFieldValue);
        }
        return true;
      }),
    );
    return result;
  }
  return filteredData;
};

export const getTextVariableStatByProjectId = createAsyncThunk(
  "textVariables/getTextVariableStatByProjectId",
  async ({ projectId, versionId }: { projectId: number; versionId: string }, { getState, rejectWithValue }) => {
    try {
      const { textVariables } = getState() as RootState;
      const allTextVariables = await textVariablesClient.getTextVariables(projectId, versionId);
      const textVariablesFilters = (textVariables.filters as TextVariableDto) ?? {};
      let filteredData: TextVariableDto[] = [...allTextVariables];
      filteredData = filterTextVariables(textVariablesFilters, filteredData);
      return {
        allTextVariables,
        filteredData,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getOpenEndVariableWaves = createAsyncThunk(
  "textVariables/getOpenEndVariableWaves",
  async ({ projectId }: { projectId: number; },
    { rejectWithValue }) => {
    try {
      const result = await textVariablesClient.getOpenEndVariableWaves(projectId);
      return result
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const textVariablesSlice = createSlice({
  name: "textVariables",
  initialState: initialTextVariables,
  reducers: {
    resetTextVariables: () => initialTextVariables,
    resetTextVariablesLoadingStatus: (state) => {
      state.loadingTextVariables = LoadingState.idle
    },
    setTextDataFilters: (state, { payload }: { payload: { [key in keyof TextVariableDto]: string[] } }) => {
      state.filters = { ...state.filters, ...payload };
    },

    setTextVariablesData: (state, { payload }: { payload: TextVariableDto[]}) => {
      state.textVariables = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTextVariablesByProjectId.pending, (state) => {
      state.loadingTextVariables = LoadingState.pending;
      state.loadingTextVariablesError = undefined;
    });
    builder.addCase(getTextVariablesByProjectId.fulfilled, (state, { payload }) => {
      state.allTextVariables = payload.allTextVariables;
      state.textVariables = payload.filteredData;
      state.loadingTextVariables = LoadingState.succeed;
      state.loadingTextVariablesError = undefined;
    });
    builder.addCase(getTextVariablesByProjectId.rejected, (state, { error }) => {
      state.loadingTextVariables = LoadingState.failed;
      state.loadingTextVariablesError = error.message;
    });

    builder.addCase(getOpenEndVariableWaves.pending, (state) => {
      state.loadingWaveVariables = LoadingState.pending;
      state.loadingWaveVariablesError = undefined;
    });
    builder.addCase(getOpenEndVariableWaves.fulfilled, (state, { payload }) => {
      state.waveVariables = payload ?? [];
      state.loadingWaveVariables = LoadingState.succeed;
      state.loadingWaveVariablesError = undefined;
    });
    builder.addCase(getOpenEndVariableWaves.rejected, (state, { error }) => {
      state.loadingWaveVariables = LoadingState.failed;
      state.loadingWaveVariablesError = error.message;
    });
    builder.addCase(getTextVariableStatByProjectId.fulfilled, (state, {payload})=>{
      state.allTextVariables = payload.allTextVariables;
      state.textVariables = payload.filteredData;
    });
  },
});
export const {
  resetTextVariables,
  resetTextVariablesLoadingStatus,
  setTextDataFilters,
  setTextVariablesData,
} = textVariablesSlice.actions;
export default textVariablesSlice.reducer;
